import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Input,
  Button,
  Text,
  Link,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Eye24Regular, EyeOff24Regular, CheckmarkCircle24Filled } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';
import { isSandbox } from '../config/appEnv';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding('24px', '16px'),
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('40px'),
    boxShadow: tokens.shadow16,
    boxSizing: 'border-box',
    '@media (max-width: 480px)': {
      ...shorthands.padding('28px', '20px'),
      boxShadow: tokens.shadow8,
    },
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logoImage: {
    height: '48px',
    '@media (max-width: 480px)': {
      height: '40px',
    },
  },
  sandboxBadge: {
    display: 'block',
    width: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '12px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#ffffff',
    backgroundColor: '#c2410c',
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('4px'),
  },
  title: {
    textAlign: 'center' as const,
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
    display: 'block',
    '@media (max-width: 480px)': {
      fontSize: '18px',
    },
  },
  subtitle: {
    textAlign: 'center' as const,
    display: 'block',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '32px',
    '@media (max-width: 480px)': {
      marginBottom: '24px',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  hint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  error: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorStatusDangerBorder1),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('10px', '12px'),
    color: tokens.colorStatusDangerForeground1,
    fontSize: '14px',
  },
  back: {
    textAlign: 'center' as const,
    fontSize: '14px',
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center' as const,
    gap: '12px',
  },
  successIcon: {
    color: tokens.colorStatusSuccessForeground1,
    fontSize: '40px',
  },
  successText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
});

export default function ResetPassword() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiFetch('/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Could not reset your password. Please try again.');
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <img src="/logo-tradewinds.png" alt="Tradewinds Mechanical" className={styles.logoImage} />
        </div>

        {isSandbox() && <span className={styles.sandboxBadge}>SANDBOX</span>}

        {done ? (
          <div className={styles.success}>
            <CheckmarkCircle24Filled className={styles.successIcon} />
            <Text as="h1" className={styles.title}>Password reset</Text>
            <Text className={styles.successText}>
              Your password has been updated. You can now sign in with it.
            </Text>
            <Link onClick={() => navigate('/login')}>Go to sign in</Link>
          </div>
        ) : !token ? (
          <div className={styles.success}>
            <Text as="h1" className={styles.title}>Invalid reset link</Text>
            <Text className={styles.successText}>
              This link is missing its token. Request a new password reset to continue.
            </Text>
            <Link onClick={() => navigate('/forgot-password')}>Request a new link</Link>
          </div>
        ) : (
          <>
            <Text as="h1" className={styles.title}>Choose a new password</Text>
            <Text className={styles.subtitle}>Enter and confirm your new password.</Text>

            <form className={styles.form} onSubmit={handleSubmit}>
              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.field}>
                <label className={styles.label}>New password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(_, data) => setPassword(data.value)}
                  placeholder="At least 8 characters"
                  size="large"
                  disabled={submitting}
                  contentAfter={
                    <Button
                      appearance="transparent"
                      icon={showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                      onClick={() => setShowPassword(!showPassword)}
                      size="small"
                      type="button"
                    />
                  }
                />
                <Text className={styles.hint}>Must be at least 8 characters.</Text>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Confirm new password</label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(_, data) => setConfirm(data.value)}
                  placeholder="Re-enter your password"
                  size="large"
                  disabled={submitting}
                />
              </div>

              <Button appearance="primary" type="submit" size="large" disabled={submitting}>
                {submitting ? <Spinner size="tiny" /> : 'Reset password'}
              </Button>

              <div className={styles.back}>
                <Link onClick={() => navigate('/login')} disabled={submitting}>
                  Back to sign in
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
